/*
Copyright (c) 2012-2022 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import moment from "moment-timezone"
import {
  Dual,
  PLACEHOLDER_DASH,
  StoreModelType,
} from "../constants/misc"
import { Cashout } from "../types/models/cashout"
import {
  MerchantDetail,
  StoreDetail,
} from "../types/models/merchant"
import { Order } from "../types/models/order"
import { Transaction } from "../types/models/transaction"
import {
  getCapitalByCountryName,
  getCapitalTimezoneByCountryName,
} from "../utils/locale"

export const storeDetailConvertor = (
  store: APIStoreDetail,
): StoreDetail => {
  const isIntegrated =
    store.modelType === StoreModelType.integrated
  return new StoreDetail({
    storeID: store.merchantID,
    name: store.merchantName,
    city: store.city ?? "",
    type: store.modelType,
    balance: store.currentBalanceInMin ?? 0,
    revenue: isIntegrated
      ? store.revenueInMin ?? 0
      : Number.NaN,
    earnings: isIntegrated
      ? store.netEarningsInMin ?? 0
      : Number.NaN,
    advertisements: store.advertiseFeeInMin ?? 0,
    ordersBreakdown: {
      completed: isIntegrated
        ? store.payableOrderCounts ?? 0
        : Number.NaN,
      failed: 0,
      cancelled: 0,
    },
    transactionsBreakdown: {
      autoPaid: 0,
      manuallyPaid: 0,
      notPaid: 0,
    },
    active: !store.status
      ? false
      : store.status.toLowerCase() === Dual.yes,
    currencyCode: store.currency,
    timezone: store.timezone ?? "",
    adjustments: store.adjustments ?? 0,
    grabPayID: store.grabPayGrabID ?? "",
    merchantID: store.merchantGroupID ?? "",
    merchantName: store.merchantGroupName ?? "",
    tags: store.tags ?? [],
    specialMerchantType:
      store.specialMerchantType,
    isIntegratedPartner:
      store.isIntegratedPartner,
    useSpecialTooltip: store.useSpecialTooltip,
  })
}

export const merchantDetailV2Convertor = (
  merchant: APIMerchantDetailV2,
): MerchantDetail =>
  new MerchantDetail({
    merchantID: merchant.merchantGroupID,
    name: merchant.merchantGroupName,
    country: merchant.country ?? "",
    revenue: 0,
    earnings: 0,
    advertisements: 0,
    ordersBreakdown: {
      completed: 0,
      failed: 0,
      cancelled: 0,
    },
    transactionsBreakdown: {
      autoPaid: 0,
      manuallyPaid: 0,
      notPaid: 0,
    },
    active: !merchant.status
      ? false
      : merchant.status.toLowerCase() ===
        Dual.yes,
    currencyCode: merchant.currency,
    timezone: getCapitalTimezoneByCountryName(
      merchant.country,
    ),
    adjustments: 0,
    grabPayID: merchant.grabPayGrabID ?? "",
    capital: getCapitalByCountryName(
      merchant.country,
    ),
  })

export const cashoutsConvertor = (
  cashouts: Array<APICashout> | null | undefined,
): Array<Cashout> => {
  return (cashouts ?? []).map(
    res =>
      new Cashout(
        moment(res.createdAt).valueOf(),
        moment(res.updatedAt).valueOf(),
        res.merchantID,
        res.cashoutID,
        res.priceInMin ?? 0,
        res.status,
        res.bankName ?? PLACEHOLDER_DASH,
        res.last5BankAccountDigits ??
          PLACEHOLDER_DASH,
        res.bankStatementCode ?? PLACEHOLDER_DASH,
        res.grabPayID ?? "",
        res.failureReason ?? "",
      ),
  )
}

export const ordersConvertor = (
  orders: Array<APIOrder> | null | undefined,
): Array<Order> => {
  return (orders ?? []).map(
    res =>
      new Order({
        createdTime: moment(
          res.createdAt,
        ).valueOf(),
        updatedTime: moment(
          res.updatedAt,
        ).valueOf(),
        storeID: res.merchantID,
        longOrderID: res.orderID,
        shortOrderID:
          res.shortOrderID ?? PLACEHOLDER_DASH,
        bookingID: res.bookingCode,
        orderType:
          res.orderPaymentType ??
          PLACEHOLDER_DASH,
        amount: res.amount ?? 0,
        orderValue: res.orderValue ?? 0,
        gfCommission: res.commission ?? 0,
        mexDiscount: res.mexDiscount ?? 0,
        withholdingTax: res.withholdTax ?? 0,
        earnings: res.netEarning ?? 0,
        tax: res.tax ?? 0,
        adjustments: res.manualAdjustment ?? 0,
        deliveryFee: res.mexDeliveryDiscount ?? 0,
        payMethod:
          res.payMethod ?? PLACEHOLDER_DASH,
        status: res.status,
        txnIDs: res.linkedTxIDs ?? [],
        gkCommission: res.gkCommission ?? 0,
        stepUpGFCommission:
          res.stepUpCommission ?? 0,
        stepUpGKCommission:
          res.stepUpGKCommission ?? 0,
        channelType:
          res.channelType ?? PLACEHOLDER_DASH,
        channelCommission:
          res.channelCommission ?? 0,
        deliveryCommission:
          res.deliveryCommission ?? 0,
        totalCommission: 0,
        mexDeliveryCharge:
          res.mexDeliveryCharge ?? 0,
        additionalInfo: "",
        deliveryType:
          res.deliveryType ?? PLACEHOLDER_DASH,
        merchantCharge: res.deliveryType !== "DINEIN" && res.merchantCharge ? res.merchantCharge : 0,
        serviceCharge: res.deliveryType === "DINEIN" && res.merchantCharge ? res.merchantCharge : 0,
      }),
  )
}

export const transactionsConvertor = (
  transactions:
    | Array<APITransaction>
    | null
    | undefined,
): Array<Transaction> => {
  return (transactions ?? []).map(res => {
    return new Transaction(
      moment(res.createdAt).valueOf(),
      moment(res.updatedAt).valueOf(),
      res.merchantID,
      res.transactionID,
      res.orderID ?? PLACEHOLDER_DASH,
      res.shortOrderID ?? PLACEHOLDER_DASH,
      res.transactionType ?? PLACEHOLDER_DASH,
      res.transactionCategory ?? PLACEHOLDER_DASH,
      res.priceInMin ?? 0,
      res.status,
      res.payMethod ?? PLACEHOLDER_DASH,
      res.description ?? PLACEHOLDER_DASH,
      res.revenueInMin ?? Number.NaN,
      res.taxInMin ?? Number.NaN,
    )
  })
}
